import type { UserRole } from '../types';

export interface RolePermissions {
  // Navigation
  canAccessDashboard: boolean;
  canAccessFeedback: boolean;
  canAccessIdeas: boolean;
  canAccessProductDefinition: boolean;
  canAccessClients: boolean;
  canAccessIntegrations: boolean;
  canAccessSettings: boolean;

  // Settings submenu
  canAccessGeneralSettings: boolean;
  canAccessBillingSettings: boolean;
  canAccessUserManagement: boolean;
  canAccessSecuritySettings: boolean;
  canAccessNotifications: boolean;

  // Data operations
  canCreateFeedback: boolean;
  canUpdateFeedback: boolean;
  canDeleteFeedback: boolean;

  canCreateIdeas: boolean;
  canUpdateIdeas: boolean;
  canDeleteIdeas: boolean;

  canCreateClients: boolean;
  canUpdateClients: boolean;
  canDeleteClients: boolean;

  canManageProductStructure: boolean;
  canConfigureIntegrations: boolean;
  canManageUsers: boolean;
}

export const rolePermissions: Record<UserRole, RolePermissions> = {
  admin: {
    // Navigation - Full access to everything
    canAccessDashboard: true,
    canAccessFeedback: true,
    canAccessIdeas: true,
    canAccessProductDefinition: true,
    canAccessClients: true,
    canAccessIntegrations: true,
    canAccessSettings: true,

    // Settings - Full access
    canAccessGeneralSettings: true,
    canAccessBillingSettings: true,
    canAccessUserManagement: true,
    canAccessSecuritySettings: true,
    canAccessNotifications: true,

    // Data operations - Full access
    canCreateFeedback: true,
    canUpdateFeedback: true,
    canDeleteFeedback: true,

    canCreateIdeas: true,
    canUpdateIdeas: true,
    canDeleteIdeas: true,

    canCreateClients: true,
    canUpdateClients: true,
    canDeleteClients: true,

    canManageProductStructure: true,
    canConfigureIntegrations: true,
    canManageUsers: true,
  },

  product_manager: {
    // Navigation - Access to everything except Integrations
    canAccessDashboard: true,
    canAccessFeedback: true,
    canAccessIdeas: true,
    canAccessProductDefinition: true,
    canAccessClients: true,
    canAccessIntegrations: false,
    canAccessSettings: true,

    // Settings - No access to General, Billing, Security
    canAccessGeneralSettings: false,
    canAccessBillingSettings: false,
    canAccessUserManagement: true,
    canAccessSecuritySettings: false,
    canAccessNotifications: true,

    // Data operations - Full CRUD access
    canCreateFeedback: true,
    canUpdateFeedback: true,
    canDeleteFeedback: true,

    canCreateIdeas: true,
    canUpdateIdeas: true,
    canDeleteIdeas: true,

    canCreateClients: true,
    canUpdateClients: true,
    canDeleteClients: true,

    canManageProductStructure: true,
    canConfigureIntegrations: false,
    canManageUsers: true,
  },

  contributor: {
    // Navigation - View Dashboard and Product Definition only, full CRUD on others
    canAccessDashboard: true,
    canAccessFeedback: true,
    canAccessIdeas: true,
    canAccessProductDefinition: true,
    canAccessClients: true,
    canAccessIntegrations: false,
    canAccessSettings: false,

    // Settings - No access at all
    canAccessGeneralSettings: false,
    canAccessBillingSettings: false,
    canAccessUserManagement: false,
    canAccessSecuritySettings: false,
    canAccessNotifications: false,

    // Data operations - Full CRUD on Feedback, Ideas, and Clients
    canCreateFeedback: true,
    canUpdateFeedback: true,
    canDeleteFeedback: true,

    canCreateIdeas: true,
    canUpdateIdeas: true,
    canDeleteIdeas: true,

    canCreateClients: true,
    canUpdateClients: true,
    canDeleteClients: true,

    canManageProductStructure: false,
    canConfigureIntegrations: false,
    canManageUsers: false,
  },

  viewer: {
    // Navigation - View only access
    canAccessDashboard: true,
    canAccessFeedback: true,
    canAccessIdeas: true,
    canAccessProductDefinition: true,
    canAccessClients: true,
    canAccessIntegrations: false,
    canAccessSettings: false,

    // Settings - No access at all
    canAccessGeneralSettings: false,
    canAccessBillingSettings: false,
    canAccessUserManagement: false,
    canAccessSecuritySettings: false,
    canAccessNotifications: false,

    // Data operations - View only, no CRUD
    canCreateFeedback: false,
    canUpdateFeedback: false,
    canDeleteFeedback: false,

    canCreateIdeas: false,
    canUpdateIdeas: false,
    canDeleteIdeas: false,

    canCreateClients: false,
    canUpdateClients: false,
    canDeleteClients: false,

    canManageProductStructure: false,
    canConfigureIntegrations: false,
    canManageUsers: false,
  },
};

export function hasPermission(
  role: UserRole | undefined,
  permission: keyof RolePermissions
): boolean {
  if (!role) return false;
  return rolePermissions[role]?.[permission] ?? false;
}

export function getRoleDisplayName(role: UserRole): string {
  switch (role) {
    case 'admin':
      return 'Admin';
    case 'product_manager':
      return 'Product Manager';
    case 'contributor':
      return 'Contributor';
    case 'viewer':
      return 'Viewer';
    default:
      return 'Unknown';
  }
}

export function getRoleDescription(role: UserRole): string {
  switch (role) {
    case 'admin':
      return 'Full access to everything including settings, integrations, and all operations';
    case 'product_manager':
      return 'Full CRUD access to feedback, ideas, clients, and product. Can manage users. No access to integrations or key settings';
    case 'contributor':
      return 'Full CRUD on feedback, ideas, and clients. View-only access to dashboard and product definition';
    case 'viewer':
      return 'View-only access to dashboard, feedback, ideas, product definition, and client management';
    default:
      return '';
  }
}

